import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuOverlay from '../components/MenuOverlay';
import { fetchInstagramPosts } from '../services/instagramApi';

const More = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Instagram posts on mount
  useEffect(() => {
    const loadInstagramPosts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const posts = await fetchInstagramPosts(12);
        setInstagramPosts(posts);
      } catch (err) {
        setError('Failed to load Instagram posts');
        console.error('Instagram fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadInstagramPosts();
  }, []);

  useEffect(() => {
    // Allow scrolling on this page
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    if (!isMenuOpen) {
      html.style.overflow = 'auto';
      html.style.height = 'auto';
      body.style.overflow = 'auto';
      body.style.height = 'auto';
      body.style.position = 'static';
      if (root) {
        root.style.overflow = 'auto';
        root.style.height = 'auto';
        root.style.position = 'static';
      }
    } else {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      if (root) {
        root.style.overflow = 'hidden';
      }
    }

    // Cleanup on unmount - restore original state
    return () => {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.position = 'fixed';
      if (root) {
        root.style.overflow = 'hidden';
        root.style.height = '100%';
        root.style.position = 'fixed';
      }
    };
  }, [isMenuOpen]);

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  // Mock YouTube videos - structure ready for real API data
  const youtubeVideos = [
    {
      id: '1',
      title: 'Latest Film Project',
      thumbnail: '/images/main-background.jpg',
      link: 'https://www.youtube.com/@joshuagavinn'
    },
    {
      id: '2',
      title: 'Behind The Scenes',
      thumbnail: '/images/images.png',
      link: 'https://www.youtube.com/@joshuagavinn'
    },
    {
      id: '3',
      title: 'Creative Process',
      thumbnail: '/images/main-background.jpg',
      link: 'https://www.youtube.com/@joshuagavinn'
    }
  ];

  return (
    <>
      {/* Global Navigation - Always Visible */}
      <nav className="fixed top-0 left-0 right-0 z-30 p-8 md:p-10 lg:p-10">
        <div className="flex items-center justify-between w-full">
          {/* Left spacer for centering */}
          <div className="w-20"></div>

          {/* Center title - Home button */}
          <div className="flex-1 flex justify-center">
            <h1
              onClick={handleHomeClick}
              className="text-white text-lg md:text-xl lg:text-2xl font-light tracking-widest uppercase cursor-pointer"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Joshua Gavin
            </h1>
          </div>

          {/* Right menu icon */}
          <button
            onClick={handleMenuOpen}
            className="w-20 flex flex-col items-end gap-2 bg-transparent border-none cursor-pointer p-0 group"
            aria-label="Open menu"
          >
            <span
              className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            ></span>
            <span
              className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            ></span>
            {isMobile && (
              <span
                className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
                style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
              ></span>
            )}
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative bg-black min-h-screen pt-32 pb-20 px-4 md:px-8 lg:px-12">
        {/* Instagram Section */}
        <section className="relative z-0 mb-32">
          <h2
            className="text-white text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-wider text-center mb-16"
            style={{
              fontFamily: 'Montserrat, sans-serif'
            }}
          >
            Latest Work
          </h2>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-20">
              <div className="text-white text-xl" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Loading...
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="flex justify-center items-center py-20">
              <div className="text-gray-400 text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {error}
              </div>
            </div>
          )}

          {/* Instagram Grid */}
          {!isLoading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {instagramPosts.map((post) => (
                <article
                  key={post.id}
                  className="relative aspect-square overflow-hidden group cursor-pointer"
                >
                  {/* Image with zoom effect */}
                  <div className="relative w-full h-full z-10">
                    <img
                      src={post.imageUrl}
                      alt={post.caption}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>

                  {/* Dark overlay on hover */}
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-70 transition-opacity duration-700 ease-out z-20"></div>

                  {/* Caption and metadata - revealed on hover */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out z-20">
                    <p
                      className="text-white text-sm md:text-base mb-2 line-clamp-2"
                      style={{
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                    >
                      {post.caption}
                    </p>
                    <time
                      className="text-gray-300 text-xs md:text-sm mb-3"
                      dateTime={post.date}
                      style={{
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                    >
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </time>
                    <a
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-xs md:text-sm uppercase tracking-wider hover:opacity-70 transition-opacity duration-300"
                      style={{
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      View on Instagram →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* YouTube Section */}
        <section className="relative z-0">
          {/* Decorative top bar on mobile */}
          {isMobile && (
            <div
              className="w-full h-16 mb-8"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)'
              }}
            />
          )}

          <h2
            className="text-white text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-wider text-center mb-16"
            style={{
              fontFamily: 'Montserrat, sans-serif'
            }}
          >
            Featured Videos
          </h2>

          {/* YouTube Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
            {youtubeVideos.map((video) => (
              <article
                key={video.id}
                className="relative group cursor-pointer"
              >
                <a
                  href={video.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  {/* Video thumbnail */}
                  <div className="relative aspect-video overflow-hidden mb-4">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-80"
                      loading="lazy"
                    />
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 z-20">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-white border-b-8 border-b-transparent ml-1"></div>
                      </div>
                    </div>
                  </div>

                  {/* Video title */}
                  <h3
                    className="text-white text-base md:text-xl font-medium tracking-wide mb-2 md:mb-4"
                    style={{
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    {video.title}
                  </h3>
                </a>
              </article>
            ))}
          </div>
        </section>
      </main>

      {/* Menu Overlay */}
      <MenuOverlay isOpen={isMenuOpen} onClose={handleMenuClose} />
    </>
  );
};

export default More;
